import { useEffect, useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MaintenanceRequiredSelect } from '@/products/maintenance/features/maintenance-filter-select';
import {
  useMaintenanceInstallation,
  useMaintenanceInstallations,
} from '@/products/maintenance/features/use-maintenance';

const SEARCH_DEBOUNCE_MS = 300;

export function MaintenanceInstallationSearchSelect({
  value,
  onChange,
  disabled,
  excludeIds,
}: {
  value: string;
  onChange: (installationId: string) => void;
  disabled?: boolean;
  excludeIds?: readonly string[];
}) {
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const selected = useMaintenanceInstallation(value);
  const list = useMaintenanceInstallations({
    page: 1,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    q: q || undefined,
  });

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(draft.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft]);

  const selectedLabel = selected.data
    ? `${selected.data.installationNumber} – ${selected.data.name}`
    : null;

  const excluded = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);

  const options = useMemo(() => {
    const fromList = (list.data?.items ?? [])
      .filter((row) => !excluded.has(row.id) || row.id === value)
      .map((row) => ({
        value: row.id,
        label: `${row.installationNumber} – ${row.name}`,
      }));
    if (value && selectedLabel && !fromList.some((row) => row.value === value)) {
      return [{ value, label: selectedLabel }, ...fromList];
    }
    return fromList;
  }, [excluded, list.data?.items, selectedLabel, value]);

  return (
    <div className="space-y-2">
      <Label htmlFor="wo-installation-search">Anlage suchen</Label>
      <Input
        id="wo-installation-search"
        className="min-h-11"
        value={draft}
        disabled={disabled}
        placeholder="Kennung oder Bezeichnung"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            setQ(draft.trim());
          }
        }}
      />
      <MaintenanceRequiredSelect
        label="Anlage"
        id="wo-installation"
        value={value}
        onValueChange={onChange}
        items={options}
        disabled={Boolean(disabled) || list.isPending}
      />
    </div>
  );
}
